import React from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Container, IconButton, Drawer, List, ListItemButton, ListItemText, Divider, Collapse, Typography } from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import LanguageSwitcher from "./LanguageSwitcher";
import Logo from "./Logo";
import { useAuth } from "../context/AuthContext";
import { THEMES } from "../content/themes";
import { Lang } from "../content/mutualiteiten";

/** Hoofdnavigatie met flyout-panelen (referentiepatroon:
 *  vindhulp-web VindHulpHeader nav-dropdown/nav-flyout):
 *  - desktop: hover én focus openen het paneel; Escape sluit; de trigger
 *    blijft een gewone link (klik navigeert naar de overzichtspagina);
 *  - mobiel: dezelfde groepen als accordion in de bestaande Drawer. */

interface NavItem { label: string; to: string }
interface NavGroup { key: string; to: string; items: NavItem[] }

function useNavGroups(): NavGroup[] {
  const { t, i18n } = useTranslation();
  const lang = ((i18n.language || "nl").slice(0, 2) as Lang);
  return [
    {
      key: "findHelp",
      to: "/hoe-het-werkt",
      items: [
        { label: t("navMenu.start"), to: "/start" },
        { label: t("navMenu.howItWorks"), to: "/hoe-het-werkt" },
        { label: t("navMenu.howWeMatch"), to: "/hoe-matchen-wij" },
        { label: t("navMenu.reimbursement"), to: "/terugbetaling" },
        { label: t("navMenu.pricing"), to: "/tarieven" },
        { label: t("navMenu.quality"), to: "/kwaliteit-en-erkenning" },
        { label: t("navMenu.faq"), to: "/veelgestelde-vragen" },
      ],
    },
    {
      key: "selfHelp",
      to: "/zelfhulp",
      items: [
        { label: t("navMenu.courses"), to: "/zelfhulp" },
        ...THEMES.map((th) => ({ label: th.nav[lang] || th.nav.nl, to: `/thema/${th.slug}` })),
      ],
    },
    {
      key: "forEmployers",
      to: "/voor-werkgevers",
      items: [
        { label: t("navMenu.employersPage"), to: "/voor-werkgevers" },
        { label: t("navMenu.barometer"), to: "/welzijnsbarometer" },
        { label: t("navMenu.privacyStandard"), to: "/privacystandaard" },
      ],
    },
    {
      key: "forPsychologists",
      to: "/voor-psychologen",
      items: [
        { label: t("navMenu.psychologistsPage"), to: "/voor-psychologen" },
        { label: t("navMenu.quality"), to: "/kwaliteit-en-erkenning" },
        { label: t("navMenu.howWeMatch"), to: "/hoe-matchen-wij" },
      ],
    },
  ];
}

// Compact text-button style so nav items never wrap.
const navBtnSx = {
  color: "text.primary",
  textTransform: "none",
  fontWeight: 500,
  fontSize: "0.95rem",
  whiteSpace: "nowrap",
  px: 1.25,
  minHeight: 40,
  minWidth: 0,
} as const;

/** Eén desktop-navitem met flyout-paneel (hover + focus + Escape). */
function NavFlyout({ group }: { group: NavGroup }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement | null>(null);

  // Sluit wanneer de focus het hele dropdown-blok verlaat (toetsenbord).
  const onBlur = (e: React.FocusEvent) => {
    if (!wrapRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
  };

  return (
    <Box
      ref={wrapRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={onBlur}
      onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
      sx={{ position: "relative" }}
    >
      <Button
        component={RouterLink}
        to={group.to}
        aria-haspopup="true"
        aria-expanded={open}
        endIcon={<ExpandMoreRoundedIcon sx={{ fontSize: 18, opacity: 0.6 }} />}
        sx={navBtnSx}
      >
        {t(`nav.${group.key}`)}
      </Button>
      {open && (
        <Box
          role="menu"
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            minWidth: 320,
            bgcolor: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-lg)",
            boxShadow: "var(--shadow-md)",
            p: 2.5,
            zIndex: 30,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: ".95rem", mb: 0.5 }}>{t(`navMenu.${group.key}Title`)}</Typography>
          <Typography sx={{ fontSize: ".85rem", color: "text.secondary", mb: 1.5, lineHeight: 1.55 }}>{t(`navMenu.${group.key}Desc`)}</Typography>
          <Box sx={{ display: "grid", gap: 0.25 }}>
            {group.items.map((item) => (
              <Box
                key={item.to + item.label}
                component={RouterLink}
                to={item.to}
                role="menuitem"
                onClick={() => setOpen(false)}
                sx={{
                  display: "block",
                  px: 1.25,
                  py: 0.75,
                  borderRadius: "var(--radius-sm, 8px)",
                  color: "text.primary",
                  textDecoration: "none",
                  fontSize: ".92rem",
                  "&:hover, &:focus-visible": { bgcolor: "var(--primary-50)", color: "primary.dark" },
                }}
              >
                {item.label}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = React.useState(false);
  // Mobiel: welke accordiongroep staat open?
  const [expanded, setExpanded] = React.useState<string | null>(null);
  const groups = useNavGroups();

  const closeDrawer = () => { setOpen(false); setExpanded(null); };

  return (
    <Box component="header" sx={{ borderBottom: "1px solid var(--border)", bgcolor: "rgba(250,243,233,0.85)", backdropFilter: "blur(8px)", position: "sticky", top: 0, zIndex: 20 }}>
      <Container maxWidth="lg" sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.25 }}>
        <Box component={RouterLink} to="/" aria-label="Carefunded" sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
          <Logo height={42} />
        </Box>

        {/* Desktop nav — full bar only on large screens to avoid cramping. */}
        <Box sx={{ display: { xs: "none", lg: "flex" }, alignItems: "center", gap: 0.25, ml: 3 }}>
          {groups.map((g) => (
            <NavFlyout key={g.key} group={g} />
          ))}
        </Box>

        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ display: { xs: "none", lg: "flex" }, alignItems: "center", gap: 0.75 }}>
            <LanguageSwitcher />
            {user && (
              <Button component={RouterLink} to="/dashboard" sx={navBtnSx}>{t("dashboard.nav")}</Button>
            )}
            {user?.role === "platform_admin" && (
              <Button component={RouterLink} to="/admin" sx={navBtnSx}>{t("admin.nav")}</Button>
            )}
            {user ? (
              <Button onClick={() => logout()} sx={navBtnSx}>{t("auth.logout")}</Button>
            ) : (
              <Button component={RouterLink} to="/inloggen" sx={navBtnSx}>{t("nav.login")}</Button>
            )}
            {/* Terracotta i.p.v. goud: wit op #C8381E haalt AA (goud niet). */}
            <Button variant="contained" onClick={() => navigate("/start")} sx={{ textTransform: "none", whiteSpace: "nowrap", px: 2.25, py: 0.5, minHeight: 38, fontSize: "0.95rem", bgcolor: "primary.main", color: "#fff", "&:hover": { bgcolor: "primary.dark" } }}>
              {t("nav.cta")}
            </Button>
          </Box>
          {/* Below lg → hamburger */}
          <Box sx={{ display: { xs: "flex", lg: "none" }, alignItems: "center", gap: 0.5 }}>
            <LanguageSwitcher dense />
            <IconButton onClick={() => setOpen(true)} aria-label="Open menu"><MenuRoundedIcon /></IconButton>
          </Box>
        </Box>
      </Container>

      <Drawer anchor="right" open={open} onClose={closeDrawer} PaperProps={{ sx: { width: 300, bgcolor: "background.default" } }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
          <IconButton onClick={closeDrawer} aria-label="Close menu"><CloseRoundedIcon /></IconButton>
        </Box>
        <List>
          {/* Mobiel: dezelfde groepen als accordion. */}
          {groups.map((g) => (
            <React.Fragment key={g.key}>
              <ListItemButton
                onClick={() => setExpanded(expanded === g.key ? null : g.key)}
                aria-expanded={expanded === g.key}
              >
                <ListItemText primary={t(`nav.${g.key}`)} primaryTypographyProps={{ fontWeight: 600 }} />
                {expanded === g.key ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
              </ListItemButton>
              <Collapse in={expanded === g.key} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {g.items.map((item) => (
                    <ListItemButton key={item.to + item.label} component={RouterLink} to={item.to} onClick={closeDrawer} sx={{ pl: 4 }}>
                      <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: ".95rem" }} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          ))}
          {user && (
            <ListItemButton component={RouterLink} to="/dashboard" onClick={closeDrawer}>
              <ListItemText primary={t("dashboard.nav")} />
            </ListItemButton>
          )}
          <Divider sx={{ my: 1 }} />
          {user ? (
            <ListItemButton onClick={() => { closeDrawer(); logout(); }}>
              <ListItemText primary={t("auth.logout")} />
            </ListItemButton>
          ) : (
            <ListItemButton component={RouterLink} to="/inloggen" onClick={closeDrawer}>
              <ListItemText primary={t("nav.login")} />
            </ListItemButton>
          )}
        </List>
        <Box sx={{ p: 2, mt: "auto" }}>
          <Button fullWidth variant="contained" onClick={() => { closeDrawer(); navigate("/start"); }} sx={{ textTransform: "none", bgcolor: "primary.main", color: "#fff", "&:hover": { bgcolor: "primary.dark" } }}>
            {t("nav.cta")}
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
}
